import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useNavigate } from 'react-router-dom'
import { apiUrl } from '../utils/api'
import { Sun, Moon } from 'lucide-react'
import '../styles/admin.css'

interface User {
  id: string
  email: string
  full_name: string
  role: string
  publisher_id: string | null
  publisher_name: string | null
  course_count: number
  created_at: string
  last_login: string
}

interface Publisher {
  id: string
  name: string
  slug: string
  logo_url: string | null
  description: string | null
  user_count: number
  created_at: string
}

interface Course {
  id: string
  collection_api_url: string
  collection_slug: string
  collection_id: number
  title: string
  summary: string | null
  cover_image_url: string | null
  total_modules: number
  is_published: number
  created_at: string
  updated_at: string
}

interface AssignmentRow {
  id: string
  assigned_at: string
  user_id: string
  user_name: string
  user_email: string
  publisher_name: string | null
  course_id: string
  course_title: string
  total_modules: number
  completed_modules: number
  progress_percentage: number
}

type Tab = 'publishers' | 'users' | 'courses' | 'assignments'

const TABS: { id: Tab; label: string }[] = [
  { id: 'publishers', label: 'Publishers' },
  { id: 'users', label: 'Users' },
  { id: 'courses', label: 'Courses' },
  { id: 'assignments', label: 'Assignments' },
]

export default function AdminDashboard() {
  const { user, token, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('publishers')

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="header-content">
          <div className="header-title">
            <span className="header-eyebrow">ADMINISTRATION</span>
            <h1>Admin Panel</h1>
          </div>
          <div className="user-info">
            <span className="user-name">{user?.fullName}</span>
            <button onClick={() => navigate('/dashboard')} className="back-btn">
              Back to Dashboard
            </button>
            <button className="theme-toggle-btn" onClick={toggleTheme} title="Toggle theme">
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <nav className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`tab-btn ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main className="admin-content">
        {tab === 'publishers' && <PublishersPanel token={token} />}
        {tab === 'users' && <UsersPanel token={token} />}
        {tab === 'courses' && <CoursesPanel token={token} />}
        {tab === 'assignments' && <AssignmentsPanel token={token} />}
      </main>
    </div>
  )
}

function CoursesPanel({ token }: { token: string | null }) {
  const [courses, setCourses] = useState<Course[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  const [collectionApiUrl, setCollectionApiUrl] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState('')

  const fetchCourses = async () => {
    setIsLoading(true)
    try {
      const res = await fetch(apiUrl('/api/admin/courses'), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to fetch courses')
      const data = await res.json()
      setCourses(Array.isArray(data.courses) ? data.courses : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load courses')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCourses()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsSubmitting(true)
    try {
      const res = await fetch(apiUrl('/api/admin/courses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ collectionApiUrl: collectionApiUrl.trim() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create course')
      setCollectionApiUrl('')
      await fetchCourses()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create course')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this course?')) return
    try {
      const res = await fetch(apiUrl(`/api/admin/courses/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to delete course')
      await fetchCourses()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete course')
    }
  }

  const handleRefresh = async (id: string) => {
    try {
      const res = await fetch(apiUrl(`/api/admin/courses/${id}/refresh`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to refresh course')
      await fetchCourses()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh course')
    }
  }

  return (
    <section className="courses-admin-section">
      <h2>Manage Courses</h2>

      <div className="create-course-card">
        <h3>Add a Course from Bold CMS</h3>
        <p className="hint">
          Paste a Bold collection API URL. The course title, summary, cover image and
          module count are pulled from the collection automatically.
        </p>
        <form onSubmit={handleCreate} className="create-course-form">
          <input
            type="url"
            placeholder="https://your-site.quintype.io/api/v1/collections/your-collection"
            value={collectionApiUrl}
            onChange={(e) => setCollectionApiUrl(e.target.value)}
            required
            disabled={isSubmitting}
          />
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Importing…' : 'Import Course'}
          </button>
        </form>
        {formError && <div className="error-message">{formError}</div>}
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading">Loading courses…</div>
      ) : courses.length === 0 ? (
        <div className="no-courses">
          <p>No courses yet. Import one from a Bold collection above.</p>
        </div>
      ) : (
        <div className="course-admin-grid">
          {courses.map((c) => (
            <CourseAdminCard
              key={c.id}
              course={c}
              token={token}
              onRefresh={() => handleRefresh(c.id)}
              onDelete={() => handleDelete(c.id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

interface AssignedUser {
  id: string
  email: string
  full_name: string
  assigned_at: string
}

interface AvailableUser {
  id: string
  email: string
  full_name: string
}

function CourseAdminCard({
  course: c,
  token,
  onRefresh,
  onDelete,
}: {
  course: Course
  token: string | null
  onRefresh: () => void
  onDelete: () => void
}) {
  const [showAssign, setShowAssign] = useState(false)
  const [assigned, setAssigned] = useState<AssignedUser[]>([])
  const [available, setAvailable] = useState<AvailableUser[]>([])
  const [selectedUser, setSelectedUser] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const fetchAssignments = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetch(apiUrl(`/api/admin/courses/${c.id}/assignments`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to load assignments')
      setAssigned(Array.isArray(data.assigned) ? data.assigned : [])
      setAvailable(Array.isArray(data.available) ? data.available : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignments')
    } finally {
      setLoading(false)
    }
  }

  const toggleAssign = () => {
    const next = !showAssign
    setShowAssign(next)
    if (next) fetchAssignments()
  }

  const handleAssign = async () => {
    if (!selectedUser) return
    try {
      const res = await fetch(apiUrl(`/api/admin/courses/${c.id}/assign`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId: selectedUser }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to assign')
      }
      setSelectedUser('')
      await fetchAssignments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign')
    }
  }

  const handleUnassign = async (userId: string) => {
    try {
      const res = await fetch(apiUrl(`/api/admin/courses/${c.id}/assign/${userId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to unassign')
      await fetchAssignments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unassign')
    }
  }

  return (
    <div className="course-admin-card">
      <div
        className="course-admin-cover"
        style={c.cover_image_url ? { backgroundImage: `url(${c.cover_image_url})` } : undefined}
      >
        {!c.cover_image_url && <span className="no-cover">No cover</span>}
      </div>
      <div className="course-admin-body">
        <h3>{c.title}</h3>
        <p className="course-admin-summary">{c.summary || 'No summary'}</p>
        <div className="course-admin-meta">
          <span className="modules-badge">{c.total_modules} modules</span>
          <span className="slug-badge">{c.collection_slug}</span>
        </div>
        <div className="course-admin-actions">
          <button className="action-btn assign" onClick={toggleAssign}>
            {showAssign ? 'Hide' : 'Assign'}
          </button>
          <button className="action-btn refresh" onClick={onRefresh}>
            Refresh
          </button>
          <button className="action-btn delete" onClick={onDelete}>
            Delete
          </button>
        </div>

        {showAssign && (
          <div className="assign-panel">
            {error && <div className="error-message">{error}</div>}
            {loading ? (
              <div className="loading small">Loading…</div>
            ) : (
              <>
                <div className="assign-add-block">
                  <select
                    className="assign-select"
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <option value="">Select user to assign…</option>
                    {available.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name} ({u.email})
                      </option>
                    ))}
                  </select>
                  <button
                    className="assign-confirm-btn"
                    onClick={handleAssign}
                    disabled={!selectedUser}
                  >
                    + Assign
                  </button>
                </div>

                {assigned.length === 0 ? (
                  <p className="assign-empty">No users assigned yet.</p>
                ) : (
                  <div className="assigned-list">
                    {assigned.map((u) => (
                      <div key={u.id} className="assigned-bar">
                        <div className="assigned-bar-info">
                          <span className="assigned-bar-name">{u.full_name}</span>
                          <span className="assigned-bar-email">{u.email}</span>
                        </div>
                        <button className="remove-x" onClick={() => handleUnassign(u.id)} title="Remove">
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function UsersPanel({ token }: { token: string | null }) {
  const [users, setUsers] = useState<User[]>([])
  const [publishers, setPublishers] = useState<Publisher[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [publisherFilter, setPublisherFilter] = useState('')
  const [editing, setEditing] = useState<User | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const authHeaders = { Authorization: `Bearer ${token}` }

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (search.trim()) params.set('search', search.trim())
      if (publisherFilter) params.set('publisher_id', publisherFilter)
      const qs = params.toString()
      const res = await fetch(apiUrl(`/api/admin/users${qs ? `?${qs}` : ''}`), {
        headers: authHeaders,
      })
      if (!res.ok) throw new Error('Failed to fetch users')
      const data = await res.json()
      setUsers(Array.isArray(data.users) ? data.users : [])
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPublishers = async () => {
    try {
      const res = await fetch(apiUrl('/api/admin/publishers'), { headers: authHeaders })
      if (!res.ok) return
      const data = await res.json()
      setPublishers(Array.isArray(data.publishers) ? data.publishers : [])
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    fetchPublishers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const t = setTimeout(fetchUsers, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, publisherFilter])

  const handleDelete = async (u: User) => {
    if (!confirm(`Delete user ${u.email}?`)) return
    try {
      const res = await fetch(apiUrl(`/api/admin/users/${u.id}`), {
        method: 'DELETE',
        headers: authHeaders,
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete user')
      }
      await fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    }
  }

  return (
    <section className="admin-panel">
      <div className="panel-toolbar">
        <div className="toolbar-filters">
          <div className="search-box">
            <span className="search-icon">⌕</span>
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select value={publisherFilter} onChange={(e) => setPublisherFilter(e.target.value)}>
            <option value="">All publishers</option>
            {publishers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <button className="add-btn" onClick={() => setShowCreate(true)}>
          + Add User
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading">Loading users…</div>
      ) : users.length === 0 ? (
        <div className="empty-state">No users found.</div>
      ) : (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>NAME</th>
                <th>EMAIL</th>
                <th>ROLE</th>
                <th>PUBLISHER</th>
                <th>COURSES</th>
                <th>CREATED</th>
                <th className="actions-col">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="cell-strong">{u.full_name}</td>
                  <td className="cell-mono">{u.email}</td>
                  <td>
                    <span className={`badge role-${u.role}`}>{u.role}</span>
                  </td>
                  <td>
                    {u.publisher_name ? (
                      <span className="badge badge-pub">{u.publisher_name}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{u.course_count}</td>
                  <td className="muted">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="actions-col">
                    <button className="row-btn" onClick={() => setEditing(u)}>
                      Edit
                    </button>
                    <button className="row-btn danger" onClick={() => handleDelete(u)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <UserModal
          token={token}
          publishers={publishers}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false)
            fetchUsers()
          }}
        />
      )}
      {editing && (
        <UserModal
          token={token}
          publishers={publishers}
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            fetchUsers()
          }}
        />
      )}
    </section>
  )
}

function UserModal({
  token,
  publishers,
  user,
  onClose,
  onSaved,
}: {
  token: string | null
  publishers: Publisher[]
  user?: User
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!user
  const [email, setEmail] = useState(user?.email || '')
  const [fullName, setFullName] = useState(user?.full_name || '')
  const [role, setRole] = useState(user?.role || 'editor')
  const [publisherId, setPublisherId] = useState(user?.publisher_id || '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const url = isEdit ? `/api/admin/users/${user!.id}` : '/api/admin/users'
      const method = isEdit ? 'PUT' : 'POST'
      const body: any = { fullName, role, publisherId: publisherId || null }
      if (!isEdit) {
        body.email = email
        body.password = password
      } else if (password) {
        body.password = password
      }
      const res = await fetch(apiUrl(url), {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to save user')
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit User' : 'Add User'}</h3>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <label>
            Full name
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isEdit}
            />
          </label>
          <div className="form-row">
            <label>
              Role
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
                <option value="editor_in_chief">Editor in Chief</option>
                <option value="tech">Tech</option>
              </select>
            </label>
            <label>
              Publisher
              <select value={publisherId} onChange={(e) => setPublisherId(e.target.value)}>
                <option value="">No publisher</option>
                {publishers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            {isEdit ? 'New password (leave blank to keep)' : 'Password'}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={!isEdit}
              minLength={6}
            />
          </label>
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function PublishersPanel({ token }: { token: string | null }) {
  const [publishers, setPublishers] = useState<Publisher[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Publisher | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const authHeaders = { Authorization: `Bearer ${token}` }

  const fetchPublishers = async () => {
    setIsLoading(true)
    try {
      const res = await fetch(apiUrl('/api/admin/publishers'), { headers: authHeaders })
      if (!res.ok) throw new Error('Failed to fetch publishers')
      const data = await res.json()
      setPublishers(Array.isArray(data.publishers) ? data.publishers : [])
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load publishers')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPublishers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDelete = async (p: Publisher) => {
    if (!confirm(`Delete publisher "${p.name}"? Its users will be unassigned.`)) return
    try {
      const res = await fetch(apiUrl(`/api/admin/publishers/${p.id}`), {
        method: 'DELETE',
        headers: authHeaders,
      })
      if (!res.ok) throw new Error('Failed to delete publisher')
      await fetchPublishers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete publisher')
    }
  }

  const filtered = publishers.filter(
    (p) =>
      !search.trim() ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.slug.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <section className="admin-panel">
      <div className="panel-toolbar">
        <div className="toolbar-filters">
          <div className="search-box">
            <span className="search-icon">⌕</span>
            <input
              type="text"
              placeholder="Search by name or slug…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <button className="add-btn" onClick={() => setShowCreate(true)}>
          + Add Publisher
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading">Loading publishers…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">No publishers found. Add one to get started.</div>
      ) : (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>NAME</th>
                <th>SLUG</th>
                <th>DESCRIPTION</th>
                <th>USERS</th>
                <th className="actions-col">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td className="cell-strong">
                    <div className="pub-name">
                      {p.logo_url && <img src={p.logo_url} alt="" className="pub-logo" />}
                      {p.name}
                    </div>
                  </td>
                  <td className="cell-mono">{p.slug}</td>
                  <td className="cell-desc">{p.description || <span className="muted">—</span>}</td>
                  <td>{p.user_count}</td>
                  <td className="actions-col">
                    <button className="row-btn" onClick={() => setEditing(p)}>
                      Edit
                    </button>
                    <button className="row-btn danger" onClick={() => handleDelete(p)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <PublisherModal
          token={token}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false)
            fetchPublishers()
          }}
        />
      )}
      {editing && (
        <PublisherModal
          token={token}
          publisher={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            fetchPublishers()
          }}
        />
      )}
    </section>
  )
}

function PublisherModal({
  token,
  publisher,
  onClose,
  onSaved,
}: {
  token: string | null
  publisher?: Publisher
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!publisher
  const [name, setName] = useState(publisher?.name || '')
  const [slug, setSlug] = useState(publisher?.slug || '')
  const [logoUrl, setLogoUrl] = useState(publisher?.logo_url || '')
  const [description, setDescription] = useState(publisher?.description || '')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const url = isEdit ? `/api/admin/publishers/${publisher!.id}` : '/api/admin/publishers'
      const method = isEdit ? 'PUT' : 'POST'
      const res = await fetch(apiUrl(url), {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, slug, logoUrl, description }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to save publisher')
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save publisher')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Publisher' : 'Add Publisher'}</h3>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            Slug
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="auto-generated from name if blank"
            />
          </label>
          <label>
            Logo URL
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://…"
            />
          </label>
          <label>
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </label>
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Publisher'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function AssignmentsPanel({ token }: { token: string | null }) {
  const [assignments, setAssignments] = useState<AssignmentRow[]>([])
  const [publishers, setPublishers] = useState<Publisher[]>([])
  const [courses, setCourses] = useState<Course[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [publisherFilter, setPublisherFilter] = useState('')
  const [courseFilter, setCourseFilter] = useState('')

  const authHeaders = { Authorization: `Bearer ${token}` }

  const fetchAssignments = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (publisherFilter) params.set('publisher_id', publisherFilter)
      if (courseFilter) params.set('course_id', courseFilter)
      const qs = params.toString()
      const res = await fetch(apiUrl(`/api/admin/assignments${qs ? `?${qs}` : ''}`), {
        headers: authHeaders,
      })
      if (!res.ok) throw new Error('Failed to fetch assignments')
      const data = await res.json()
      setAssignments(Array.isArray(data.assignments) ? data.assignments : [])
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignments')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchFilters = async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        fetch(apiUrl('/api/admin/publishers'), { headers: authHeaders }),
        fetch(apiUrl('/api/admin/courses'), { headers: authHeaders }),
      ])
      if (pRes.ok) {
        const pData = await pRes.json()
        setPublishers(Array.isArray(pData.publishers) ? pData.publishers : [])
      }
      if (cRes.ok) {
        const cData = await cRes.json()
        setCourses(Array.isArray(cData.courses) ? cData.courses : [])
      }
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    fetchFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchAssignments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherFilter, courseFilter])

  return (
    <section className="admin-panel">
      <div className="panel-toolbar">
        <div className="toolbar-filters">
          <select value={publisherFilter} onChange={(e) => setPublisherFilter(e.target.value)}>
            <option value="">All publishers</option>
            {publishers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
            <option value="">All courses</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading">Loading assignments…</div>
      ) : assignments.length === 0 ? (
        <div className="empty-state">
          No assignments yet. Assign users to courses from the Courses tab.
        </div>
      ) : (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>USER</th>
                <th>PUBLISHER</th>
                <th>COURSE</th>
                <th>PROGRESS</th>
                <th>ASSIGNED</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id}>
                  <td className="cell-strong">
                    {a.user_name}
                    <div className="cell-sub">{a.user_email}</div>
                  </td>
                  <td>
                    {a.publisher_name ? (
                      <span className="badge badge-pub">{a.publisher_name}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{a.course_title}</td>
                  <td>
                    <div className="progress-cell">
                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{ width: `${a.progress_percentage}%` }}
                        />
                      </div>
                      <span className="progress-label">
                        {a.completed_modules}/{a.total_modules} ({a.progress_percentage}%)
                      </span>
                    </div>
                  </td>
                  <td className="muted">{new Date(a.assigned_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
